import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Swal from 'sweetalert2';
import { Orders } from 'src/app/models/Orders.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { AgentsService } from 'src/app/services/agents.service';
import { OrdersService } from 'src/app/services/orders.service';
import { PrinterService } from 'src/app/services/printer/printer.service';
import { Log } from 'src/app/services/shared/log';
import { environment } from 'src/environments/environment';

/**
 * Order details widget — used inside the modal opened from
 * `<app-agente-list>` and `<app-client-list>` row clicks.
 *
 * Rewritten 2026-06-09 (v2.4.0 prep). The previous implementation was
 * 378 LOC of Kendo dialog + Ionic AlertController + a FormBuilder used
 * only to drive 13 readonly inputs. None of that was needed for a
 * display-only view that doesn't save anything. The new component
 * stores the loaded order in `order` / `orderNew`, exposes a
 * `displayedProducts` getter that normalises the two backend shapes,
 * and lets the parent modal own the close gesture.
 *
 * Data flow — the four backend endpoints are preserved unchanged:
 *
 *   token_agent == CLIENT_TOKEN
 *     • status_cod === 0  → `getCustomerOrderNew`  (preview of a new
 *                                                   client order, not
 *                                                   yet assigned)
 *     • status_cod !== 0  → `getClientOrder`       (assigned client
 *                                                   order, normal flow)
 *
 *   token_agent != CLIENT_TOKEN (admin token of an agent)
 *     • status_cod === 0  → `getAgentOrderNew`     (preview of a new
 *                                                   agent order)
 *     • status_cod !== 0  → `getAgentOrder`        (assigned agent
 *                                                   order)
 *
 * Inputs come from the parent (`agent_id`, `order_id`, `token_agent`,
 * `or_assigned_date`, `status_cod`). On input change the widget
 * reloads — so the parent doesn't have to destroy/recreate it when the
 * user opens a different order.
 *
 * The widget emits `productHistory(pr_code)` when the user clicks the
 * 📜 icon on a product row; the parent decides whether to open the
 * product-history view or ignore the event.
 */
@Component({
  selector: 'app-ordini-agente-dettaggli',
  templateUrl: './ordini-agente-dettaggli.component.html',
  styleUrls: ['./ordini-agente-dettaggli.component.scss'],
  standalone: false,
})


export class OrdiniAgenteDettaggliComponent implements OnInit, OnChanges {

  private readonly agentsServ = inject(AgentsService);
  private readonly ordersServ = inject(OrdersService);
  private readonly printerServ = inject(PrinterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  // ── Inputs (from parent modal) ───────────────────────────────────
  /** Kept for backwards-compatibility; the parent always passes true. */
  @Input() openOrderDettagli = true;
  @Input() agent_id: string | number = '';
  @Input() order_id: number | string = '';
  @Input() token_agent = '';
  @Input() or_assigned_date = '';
  @Input() status_cod = 0;

  // ── Outputs ──────────────────────────────────────────────────────
  /**
   * Emitted when the user taps the 📜 icon next to a product. Payload
   * is the product's `pr_code`. The parent (agente-list / client-list)
   * can choose to open a product-history drawer or ignore.
   */
  @Output() productHistory = new EventEmitter<string>();

  // ── Data ─────────────────────────────────────────────────────────
  /** Set when fetching an *assigned* order (status_cod !== 0). */
  order: Orders.IOrder | null = null;
  /** Set when fetching a *new* order (status_cod === 0). */
  orderNew: Orders.IOrderNew | null = null;

  loading = true;
  /** Prevents reloading on every CD if the inputs didn't actually change. */
  private lastLoadedKey = '';

  // ── Lifecycle ────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadOrderDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The parent reuses this widget for different orders without
    // destroying it. When `order_id` or `token_agent` change we
    // refetch; otherwise we stay put.
    if (changes['order_id'] || changes['token_agent'] || changes['status_cod']) {
      this.loadOrderDetails();
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Template helpers.
  // ──────────────────────────────────────────────────────────────────

  /** True when we're showing the "new order" branch. */
  get isNewOrder(): boolean {
    return this.status_cod === 0;
  }

  /** The order's number, regardless of which branch loaded it. */
  get orderNumber(): string {
    return this.order?.or_order_number || this.orderNew?.or_order_number || '—';
  }

  /** Status text — for assigned orders comes from `status[]`. */
  get statusText(): string {
    if (this.isNewOrder) return 'DA ASSEGNARE';
    return this.order?.status?.[0]?.tab_order_status_desc || '—';
  }

  /** Customer / shipping fields normalised across the two shapes. */
  get customer(): string {
    return this.order?.or_customer || this.orderNew?.or_customer || '—';
  }
  get shippingAddress(): string {
    return this.order?.or_shipping_address || this.orderNew?.or_shipping_address || '—';
  }
  get shippingCity(): string {
    return this.order?.or_shipping_city || this.orderNew?.or_shipping_city || '—';
  }
  get shippingZip(): string {
    return this.order?.or_shipping_zip || this.orderNew?.or_shipping_zip || '—';
  }
  get shippingProv(): string {
    return this.order?.or_shipping_prov || this.orderNew?.or_shipping_prov || '—';
  }
  get shippingCountry(): string {
    return this.order?.or_shipping_country || this.orderNew?.or_shipping_country || '—';
  }
  get carrier(): string {
    return this.order?.vs_details?.carrier_desc || this.orderNew?.or_carrier_desc || '—';
  }
  get operator(): string {
    const name = this.order?.vs_details?.ac_first_name;
    return name || '—';
  }
  get ddtNumber(): string {
    return this.order?.or_ddt_number || '—';
  }
  get ddtUrl(): string {
    return this.order?.or_url_ddt || '';
  }
  get hasDdtPdf(): boolean {
    return !!this.ddtUrl && this.ddtUrl.toLowerCase().endsWith('.pdf');
  }

  /** Order date formatted dd/MM/yyyy. */
  get orderDate(): string {
    const raw = this.order?.or_order_date || this.orderNew?.or_order_date || '';
    return this.formatDate(raw);
  }

  /** Closing date — only meaningful for assigned orders. */
  get closingDate(): string {
    const raw = this.order?.or_closing_date || '';
    return this.formatDate(raw);
  }

  /** Unified products list — works for both new + assigned orders. */
  get displayedProducts(): Array<{ description: string; qty: number; code: string }> {
    const raw =
      this.order?.products ??
      (this.orderNew?.products as unknown as Array<Record<string, unknown>>) ??
      [];
    if (!Array.isArray(raw)) return [];
    return raw.map((p) => ({
      description: String((p as any).op_description ?? ''),
      // Assigned orders use `op_qta`, new orders use `op_stock`.
      qty: Number((p as any).op_qta ?? (p as any).op_stock ?? 0),
      code: String((p as any).pr_code ?? ''),
    }));
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions.
  // ──────────────────────────────────────────────────────────────────

  /** Open the DDT PDF in a new tab. */
  openDdt(): void {
    if (!this.ddtUrl) return;
    window.open(this.ddtUrl, '_blank', 'noopener');
  }

  /**
   * Re-generate (regenerate / stamp) the DDT for this order using the
   * printer currently selected in `PrinterService`. Used by admins to
   * force a fresh DDT print. Behaviour preserved from the legacy
   * `printDDTStamp()` method but rewired through Swal2 instead of
   * Ionic AlertController.
   */
  printDdt(): void {
    if (!this.order) return;
    const printerId = this.printerServ.printerSelection?.value?.printer_ddt_id;
    if (!printerId) {
      Swal.fire({
        icon: 'warning',
        title: 'Stampante non selezionata',
        text: "Seleziona una stampante DDT dalle impostazioni prima di rigenerare il DDT.",
      });
      return;
    }
    this.ordersServ
      .getGenerateDDT(this.order.order_id, printerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (!result) return;
          if (result.error_message?.msg_code === 101) {
            Swal.fire({
              icon: 'error',
              title: 'Errore generazione DDT',
              text: "Un'altra persona potrebbe averlo generato nello stesso momento, oppure il server è occupato. Riprova fra qualche istante.",
            });
            return;
          }
          if (result.success && result.data?.or_url_ddt) {
            window.open(result.data.or_url_ddt, '_blank', 'noopener');
          }
        },
        error: (err) => {
          Log.error('[OrdiniAgenteDettaggli] generate DDT failed', err);
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: 'Impossibile generare il DDT. Riprova più tardi.',
          });
        },
      });
  }

  /** Emit a product-history request — the parent decides what to do. */
  onProductHistoryClick(code: string): void {
    if (!code) return;
    this.productHistory.emit(code);
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loader (4 backend paths, unchanged contract).
  // ──────────────────────────────────────────────────────────────────

  private loadOrderDetails(): void {
    if (!this.order_id) return;
    const key = `${this.order_id}|${this.token_agent}|${this.status_cod}`;
    if (key === this.lastLoadedKey) return;
    this.lastLoadedKey = key;

    this.order = null;
    this.orderNew = null;
    this.loading = true;
    this.cdr.markForCheck();

    const isClientToken = this.token_agent === environment.CLIENT_TOKEN;

    if (isClientToken && this.status_cod === 0) {
      this.fetchCustomerOrderNew();
    } else if (isClientToken) {
      this.fetchClientOrder();
    } else if (this.status_cod === 0) {
      this.fetchAgentOrderNew();
    } else {
      this.fetchAgentOrder();
    }
  }

  // The service signatures want `string` for `order_id` and
  // `agent_id`; our inputs are loosely typed (`string | number`) to
  // tolerate the backend sometimes serialising IDs as numbers. We
  // coerce here at the call site rather than narrowing the input.
  private get orderIdStr(): string { return String(this.order_id ?? ''); }
  private get agentIdStr(): string { return String(this.agent_id ?? ''); }

  private fetchCustomerOrderNew(): void {
    this.agentsServ
      .getCustomerOrderNew(this.orderIdStr, this.agentIdStr, this.token_agent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => this.handleNewOrder(result?.data),
        error: (err) => this.handleLoadError('getCustomerOrderNew', err),
      });
  }

  private fetchClientOrder(): void {
    this.agentsServ
      .getClientOrder(this.orderIdStr, this.token_agent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => this.handleAssignedOrder(result?.data),
        error: (err) => this.handleLoadError('getClientOrder', err),
      });
  }

  private fetchAgentOrderNew(): void {
    this.agentsServ
      .getAgentOrderNew(this.orderIdStr, this.agentIdStr, this.token_agent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => this.handleNewOrder(result?.data),
        error: (err) => this.handleLoadError('getAgentOrderNew', err),
      });
  }

  private fetchAgentOrder(): void {
    this.agentsServ
      .getAgentOrder(this.orderIdStr, this.agentIdStr, this.token_agent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => this.handleAssignedOrder(result?.data),
        error: (err) => this.handleLoadError('getAgentOrder', err),
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Private helpers.
  // ──────────────────────────────────────────────────────────────────

  private handleNewOrder(data: Orders.IOrderNew | null | undefined): void {
    this.orderNew = data ?? null;
    this.order = null;
    this.loading = false;
    this.cdr.markForCheck();
  }

  private handleAssignedOrder(data: Orders.IOrder | null | undefined): void {
    this.order = data ?? null;
    this.orderNew = null;
    this.loading = false;
    this.cdr.markForCheck();
  }

  private handleLoadError(endpoint: string, err: unknown): void {
    Log.error(`[OrdiniAgenteDettaggli] ${endpoint} failed`, err);
    this.loading = false;
    this.cdr.markForCheck();
  }

  /** Format an ISO-ish date string to dd/MM/yyyy ("—" for empty / sentinel). */
  private formatDate(value: string | number | null | undefined): string {
    if (!value) return '—';
    const s = String(value);
    if (s.startsWith('0001-01-01')) return '—';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  
}
